// components/use-case-details/use-case-workflow/use-case-workflow-section.tsx
"use client";
// // components/use-case-details/use-case-workflow/use-case-workflow-section.tsx


// v1 hard coded
// components/use-case-details/use-case-workflow-section.tsx
// "use client";

import { motion } from 'framer-motion';
import {CheckCircle, FileOutput, FileText,  } from 'lucide-react';
import React from 'react';

import AgentCard from './use-case-workflow-agent-card-component';
import ChatShowcase from './use-case-workflow-example-card';
import ProcessOverviewCard from './use-case-workflow-process-overview-card';

// Insight Card component with improved styling
const InsightCard = ({ children }: { children: React.ReactNode }) => (
  <motion.div
    className="relative bg-gradient-to-r from-fluxos-dark-2 to-fluxos-dark-3 p-6 rounded-lg my-8 shadow-md border border-gray-700"
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.5, delay: 0.2 }}
  >
    {/* Top border as a separate element for cleaner styling */}
    <div className="absolute top-0 left-0 right-0 h-2 bg-fluxos-yellow" />
    <div className="flex items-center mb-3 pt-2">
      <span className="text-fluxos-yellow font-bold text-lg">Howthef Insight:</span>
      <span className="ml-2 bg-fluxos-yellow text-black text-xs px-3 py-1 rounded-full">Pro Tip</span>
    </div>
    <p className="text-fluxos-gray text-lg">{children}</p>
  </motion.div>
);

export { InsightCard };

const TechnologyStackCard = ({ technologies }: { technologies: { category: string; items: string[]; icon: React.ReactNode }[] }) => (
  <div className="bg-gray-800 rounded-lg p-6 shadow-md mt-8">
    <h4 className="text-lg font-bold text-white mb-4">Key Technologies</h4>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {technologies.map((tech, index) => (
        <div key={index} className="bg-gray-900 p-4 rounded-lg">
          <div className="flex items-center mb-3">
            {tech.icon}
            <h5 className="text-white font-bold ml-2">{tech.category}</h5>
          </div>
          <div className="flex flex-wrap gap-2">
            {tech.items.map((item, idx) => (
              <span key={idx} className="bg-gray-800 text-xs text-white px-3 py-1 rounded">{item}</span>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const WorkflowSection = () => {
  return (
    <section className="py-16 bg-gray-850">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex justify-between items-center mb-12">
          {/* <h2 className="text-3xl font-bold text-white">
            1. Workflows<span className="text-green-500">in Action</span>
          </h2> */}

          {/* <h2 className="text-3xl font-bold text-white mb-4 relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:h-[4px] after:bg-gray-green-500 after:w-[130%]">
  1) Workflows in action
</h2> */}

            {/* <h2 className="text-3xl font-bold text-white mb-4 border-b-8 border-yellow-500 ">
              Workflows in action
            </h2> */}

<h2 className="text-3xl font-bold text-white mb-4 relative inline-block after:content-[''] after:absolute after:bottom-[-8px] after:left-0 after:h-2 after:bg-yellow-500 after:w-[120%]">
  Workflows in action
</h2>
        </div>

        {/* Workflow 1 */}
        <div className="mb-16">
          {/* <div className="mb-8 rounded-lg p-8 bg-gray-900 shadow-lg border border-gray-700"> */}
          <div className="mb-8 rounded-lg p-8 bg-gray-900 shadow-lg">
            <h3 className="text-2xl font-bold text-white mb-4">
              1.1 Workflow: Automated Zoning Updates & New Project Screening
            </h3>
            {/* <p className="text-gray-300 mb-6">
              This workflow continuously monitors municipal websites for zoning changes and automatically evaluates new properties against your criteria to identify the most promising opportunities.
            </p> */}
        <div className="text-gray-300 mb-10 space-y-4">
          <p className="text-lg">
            For architecture firms, staying current with zoning regulations is a constant challenge that directly impacts project feasibility. Municipal regulations can change without fanfare, and missing these updates can lead to costly redesigns or even project abandonment.
          </p>
          <p>
            This workflow creates a continuous intelligence system that not only monitors zoning changes across multiple municipalities but also applies this knowledge directly to your property search process. It&apos;s designed to transform what has traditionally been a labor-intensive, error-prone process into an automated, reliable system that catches opportunities your competitors might miss.
          </p>
          <p>
            Rather than manually checking websites and cross-referencing regulations, the system works around the clock to maintain an always-current database of zoning information, integrated with property data. When you need to find suitable properties for a new project, the system can immediately identify viable options based on current regulations and your specific requirements.
          </p>
        </div>
            {/* Process Overview */}
            <ProcessOverviewCard
              title="Process Overview"
              steps={[
                { 
                  description: "Land Data Brain continuously collects and indexes property and zoning data",
                  actor: "system"
                },
                { 
                  description: "Scraping Agent monitors websites for zoning regulation updates",
                  actor: "ai"
                },
                { 
                  description: "New Project Brief triggers automated property search workflow",
                  actor: "human"
                },
                { 
                  description: "Initial Screening Agent queries Land Data Brain for matching properties",
                  actor: "ai"
                },
                { 
                  description: "Team receives ranked list of most promising opportunities",
                  actor: "human"
                }
              ]}
            />

            <div className="space-y-32 mt-20">
              <div className="">
                <h4 className="text-lg font-bold text-white">Workflow Components</h4>
                <p className="text-gray-300 mt-2 text-lg">
                  The workflow is powered by a combination of AI agents and a Land Data Brain, a semantic search engine that understands the relationships between different pieces of information. It is a fully integrated system that allows you to search for properties, zoning regulations, and other relevant information all in one place.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <AgentCard
                  title="Scraping Agent"
                  agentType="scraping"
                  description="Continuously monitors and extracts zoning regulation changes from municipality websites"
                  bulletPoints={[
                    "Regularly visits municipality websites",
                    "Identifies changes to zoning regulations",
                    "Extracts relevant information (new restrictions, etc.)",
                    "Updates the Land Data Brain automatically"
                  ]}
                  model="Gemini Pro 1.0"
                  temperature="0.2"
                  trigger="Scheduled (daily) or API call"
                  output="Updated zoning data"
                  borderColor="bg-green-600"
                />

                <AgentCard
                  title="Initial Screening Agent"
                  description="Evaluates properties against project requirements to identify viable candidates"
                  agentType="analysis"
                  bulletPoints={[
                    "Queries the Land Data Brain for properties in the area",
                    "Filters out unsuitable properties (wrong zoning, size, etc.)",
                    "Calculates rough ROI projections for each viable property",
                    "Ranks properties by potential ROI and feasibility"
                  ]}
                  model="Gemini Pro 1.5+"
                  temperature="0.3"
                  trigger="New project brief submission"
                  output="Top 5-10 ranked properties"
                  borderColor="bg-purple-600"
                />
              </div>


              <div className="bg-black">
                <ChatShowcase
                  title="AI Analysis Results"
                  description="Get instant insights based on your specific requirements."
                  userQuery="We need 3-5 design concepts for the Market Street property maximizing residential units while keeping construction costs under $20M."
                  aiThinkingText="Analyzing requirements and generating options based on zoning regulations..."
                  aiResponseIntro="I've analyzed the site and generated these design concepts that meet your zoning requirements:"
                  results={[
                    {
                      name: "Design Variation A: Tower + Podium",
                      details: [
                        "120 units, $18.5M cost, 16.3% ROI, 12 floors",
                        "Zoning compliance: Fully compliant with Downtown East Mixed-Use regulations",
                        "Setbacks: 15ft front, 10ft sides, 20ft rear"
                      ],
                      metrics: [
                        { key: "Design Score", value: "92/100" },
                        { key: "Sustainability", value: "A-" }
                      ],
                      highlight: true
                    },
                    {
                      name: "Design Variation B: Courtyard Layout",
                      details: [
                        "104 units, $16.8M cost, 15.7% ROI, 8 floors",
                        "Zoning compliance: Fully compliant with stepped height requirements",
                        "FAR utilization: 97% of maximum allowable floor area ratio"
                      ],
                      metrics: [
                        { key: "Design Score", value: "88/100" },
                        { key: "Sustainability", value: "A" }
                      ],
                      highlight: true
                    },
                    {
                      name: "Design Variation C: Split Towers",
                      details: [
                        "132 units, $19.7M cost, 14.2% ROI, 10 floors",
                        "Zoning compliance: Requires minor variance for building separation",
                        "Shadow impact: Minimal impact on adjacent properties"
                      ],
                      metrics: [
                        { key: "Design Score", value: "79/100" },
                        { key: "Sustainability", value: "B+" }
                      ]
                    }
                  ]}
                  aiResponseOutro="Each design adheres to the local zoning regulations while maximizing ROI. Would you like me to generate detailed floor plans for any of these concepts?"
                  icon={<FileOutput size={20} className="text-white" />}
                />
              </div>

              {/* Key Benefits - Moved to bottom */}
              <div className="bg-gray-800 rounded-lg p-6 shadow-md">
                <h4 className="text-lg font-bold text-white mb-4">Key Benefits</h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <li className="flex items-start">
                    <CheckCircle className="text-green-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                    <div>
                      <span className="text-white font-bold">Eliminate Manual Monitoring</span>
                      <p className="text-gray-300 text-sm">Never miss a zoning change with automated scraping</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-green-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                    <div>
                      <span className="text-white font-bold">Ensure Regulatory Compliance</span>
                      <p className="text-gray-300 text-sm">Decisions based on up-to-date zoning laws</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-green-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                    <div>
                      <span className="text-white font-bold">Focus Only on Viable Properties</span>
                      <p className="text-gray-300 text-sm">Save time by filtering out unsuitable options</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-green-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                    <div>
                      <span className="text-white font-bold">Rapid Opportunity Identification</span>
                      <p className="text-gray-300 text-sm">Find high-potential properties before competitors</p>
                    </div>
                  </li>
                </ul>
              </div>

            </div>
          </div>

          <InsightCard>
            Most firms waste 70% of their research time on properties that never had a chance. This system front-loads that filtering, so you only spend time on viable options.
          </InsightCard>
        </div>



        {/* Workflow 2 */}
        <div className="mb-16">
          <div className="mb-8 rounded-lg p-8 bg-gray-900 shadow-lg border border-gray-700">
            <h3 className="text-2xl font-bold text-white mb-4">
              Workflow 2: Project Deep Dive & Design Optimization
            </h3>
            <p className="text-gray-300 mb-6">
              Once a property is selected, this workflow performs comprehensive analysis and generates optimized design options to maximize ROI while ensuring architectural quality.
            </p>

            {/* Process Overview */}
            <ProcessOverviewCard
              title="Process Overview"
              steps={[
                { 
                  description: "Deep Dive Agent extracts data from Land Data Brain for selected property",
                  actor: "ai"
                },
                { 
                  description: "Comprehensive analysis report identifies constraints and opportunities",
                  actor: "system"
                },
                { 
                  description: "Design Optimization Agent generates multiple building variations",
                  actor: "ai"
                },
                { 
                  description: "Designs are ranked by projected ROI and sustainability metrics",
                  actor: "system"
                },
                { 
                  description: "Human architects review and refine top-performing designs",
                  actor: "human",
                  hasDecision: true
                }
              ]}
            />

            <div className="space-y-8 mt-8">
              <h4 className="text-lg font-bold text-white">AI Agent Breakdown</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <AgentCard
                  title="Deep Dive Agent"
                  agentType="analysis"
                  description="Gathers and synthesizes all available data about the selected property"
                  bulletPoints={[
                    "Detailed zoning regulations",
                    "High-resolution terrain data",
                    "Comparable sales data",
                    "Construction cost estimates"
                  ]}
                  model="Gemini Pro 1.5+"
                  temperature="0.1"
                  trigger="Property selection confirmation"
                  output="Comprehensive property analysis report"
                  borderColor="bg-purple-600"
                />

                <AgentCard
                  title="Design Optimization Agent"
                  description="Creates multiple design variations optimized for ROI while respecting constraints"
                  agentType="generation"
                  bulletPoints={[
                    "Determines maximum allowable building size based on zoning",
                    "Generates multiple design variations (heights, floor plans, etc.)",
                    "Calculates construction costs, revenue, and ROI for each option",
                    "Ranks designs by profitability"
                  ]}
                  model="Gemini Pro 1.5+"
                  temperature="0.7"
                  trigger="Deep Dive analysis completion"
                  output="Ranked design options with ROI projections"
                  borderColor="bg-blue-600"
                />
              </div>
<ChatShowcase
  title="Building Design Optimization"
  description="AI generates optimal design concepts based on zoning requirements and project goals."
  userQuery="Create design options for our Downtown East parcel that maximize residential units while meeting setback requirements and staying under $20M budget."
  aiThinkingText="Analyzing zoning regulations, setback requirements, and budget constraints..."
  aiResponseIntro="I've generated three design concepts that optimize for your requirements:"
  results={[
    {
      name: "Design Variation A: Tower + Podium",
      details: [
        "120 units, $18.5M cost, 16.3% ROI, 12 floors",
        "Maximizes FAR by utilizing stepped setbacks on upper floors",
        "Ground floor retail: 8,500 sq ft with separate service access"
      ],
      metrics: [
        { key: "Design Score", value: "92/100" },
        { key: "Sustainability", value: "A-" }
      ],
      highlight: true
    },
    {
      name: "Design Variation B: Courtyard Layout",
      details: [
        "104 units, $16.8M cost, 15.7% ROI, 8 floors",
        "Central courtyard provides natural light to all units",
        "Reduced parking requirements through transit proximity bonus"
      ],
      metrics: [
        { key: "Design Score", value: "88/100" },
        { key: "Sustainability", value: "A" }
      ],
      highlight: true
    },
    {
      name: "Design Variation C: Split Towers",
      details: [
        "132 units, $19.7M cost, 14.2% ROI, 10 floors",
        "Maximizes unit count but requires parking variance",
        "Higher construction complexity impacts timeline and budget"
      ],
      metrics: [
        { key: "Design Score", value: "79/100" },
        { key: "Sustainability", value: "B+" }
      ]
    }
  ]}
  aiResponseOutro="Each design follows local zoning requirements but optimizes differently. Variation A balances unit count and ROI, while B prioritizes sustainability, and C maximizes units but with higher complexity. Would you like more detailed floor plans for any option?"
  icon={<FileOutput className="text-white" />}
/>

              {/* Key Benefits */}
              <div className="bg-gray-800 rounded-lg p-6 shadow-md">
                <h4 className="text-lg font-bold text-white mb-4">Key Benefits</h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <li className="flex items-start">
                    <CheckCircle className="text-purple-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                    <div>
                      <span className="text-white font-bold">More Design Options</span>
                      <p className="text-gray-300 text-sm">Generate 10+ variations vs traditional 2-3</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-purple-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                    <div>
                      <span className="text-white font-bold">ROI-Driven Design</span>
                      <p className="text-gray-300 text-sm">Optimize for profits while maintaining quality</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-purple-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                    <div>
                      <span className="text-white font-bold">80% Faster Process</span>
                      <p className="text-gray-300 text-sm">Reduce design phase from weeks to days</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-purple-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                    <div>
                      <span className="text-white font-bold">Data-Driven Decisions</span>
                      <p className="text-gray-300 text-sm">Base designs on proven historical performance</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <InsightCard>
            Most firms only evaluate 2-3 design options. This system can generate and analyze dozens, finding hidden value opportunities your competitors miss.
          </InsightCard>

        </div>



        {/* Workflow 3 */}
        <div className="mb-16">
          <div className="mb-8 rounded-lg p-8 bg-gray-900 shadow-lg border border-gray-700">
            <h3 className="text-2xl font-bold text-white mb-4">
              Workflow 3: Financial Error Detection
            </h3>
            <p className="text-gray-300 mb-6">
              This workflow provides an automated safeguard against costly financial errors by analyzing all financial documents for inconsistencies, calculation errors, and unusual patterns.
            </p>

            {/* Process Overview */}
            <ProcessOverviewCard
              title="Process Overview"
              steps={[
                { 
                  description: "Team members upload financial spreadsheets to secure portal",
                  actor: "human"
                },
                { 
                  description: "Error Spotting Agent scans documents for anomalies and errors",
                  actor: "ai"
                },
                { 
                  description: "System categorizes issues by confidence level and potential impact",
                  actor: "system"
                },
                { 
                  description: "Financial team reviews flagged items in priority order",
                  actor: "human",
                  hasDecision: true
                },
                { 
                  description: "System learns from human decisions to improve future detection",
                  actor: "system"
                }
              ]}
            />
            <div className="space-y-8 mt-8">
              <h4 className="text-lg font-bold text-white">AI Agent Breakdown</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <AgentCard
                  title="Error Spotting Agent"
                  agentType="detection"
                  description="Analyzes financial documents for inconsistencies, errors, and unusual patterns"
                  bulletPoints={[
                    "Checks for calculation errors",
                    "Compares numbers to historical data and benchmarks",
                    "Identifies unusual patterns",
                    "Validates formulas and dependencies"
                  ]}
                  model="Gemini Pro 1.5"
                  temperature="0.1"
                  trigger="Document upload"
                  output="Error report with confidence scores"
                  borderColor="bg-orange-600"
                />

                <AgentCard
                  title="Data Monitoring Agent"
                  agentType="monitoring"
                  description="Continuously monitors financial data for trending issues and repeated errors"
                  bulletPoints={[
                    "Historical error reports",
                    "Team correction patterns",
                    "Financial performance data",
                    "Error frequency by document type"
                  ]}
                  model="Gemini Pro 1.0"
                  temperature="0.2"
                  trigger="Scheduled (weekly) or after corrections"
                  output="Risk assessment and preventive recommendations"
                  borderColor="bg-red-600"
                />
              </div>

              <ChatShowcase
                title="Financial Error Detection"
                description="AI identifies calculation errors and inconsistencies in project financial documents."
                userQuery="Review the Q1 financial projections for the Westside Tower project and identify any errors or inconsistencies."
                aiThinkingText="Scanning financial documents for errors, inconsistencies, and anomalies..."
                aiResponseIntro="I've analyzed your financial projections and found several issues that require attention:"
                results={[
                  {
                    name: "Critical Error: Construction Cost Calculation",
                    details: [
                      "Spreadsheet formula error in cell G24 underestimates costs by $1.2M",
                      "Error impacts overall project ROI calculation by 3.2%",
                      "Reference to outdated material costs from 2022 price sheet"
                    ],
                    metrics: [
                      { key: "Confidence", value: "98%" },
                      { key: "Financial Impact", value: "High" }
                    ],
                    highlight: true
                  },
                  {
                    name: "Warning: Inconsistent Financing Assumptions",
                    details: [
                      "Interest rate assumptions differ between cash flow and loan sheets",
                      "4.5% used in cash flow vs. 5.2% in debt service calculations",
                      "Results in $430K underestimation of 5-year financing costs"
                    ],
                    metrics: [
                      { key: "Confidence", value: "87%" },
                      { key: "Financial Impact", value: "Medium" }
                    ],
                    highlight: true
                  },
                  {
                    name: "Anomaly: Unusual Tenant Improvement Allocation",
                    details: [
                      "TI allowance significantly higher than company benchmarks",
                      "$85/sq ft vs. typical $65/sq ft for similar properties",
                      "May be intentional but requires verification with leasing team"
                    ],
                    metrics: [
                      { key: "Confidence", value: "76%" },
                      { key: "Financial Impact", value: "Medium-Low" }
                    ]
                  }
                ]}
                aiResponseOutro="Would you like me to generate a detailed report on these issues with recommended corrections? I can also check the document for additional lower-priority inconsistencies."
                icon={<FileText className="text-white" />}
              />

              {/* Key Benefits */}
              <div className="bg-gray-800 rounded-lg p-6 shadow-md">
                <h4 className="text-lg font-bold text-white mb-4">Key Benefits</h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <li className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                    <div>
                      <span className="text-white font-bold">Prevent Costly Mistakes</span>
                      <p className="text-gray-300 text-sm">Catch errors before they impact project financials</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                    <div>
                      <span className="text-white font-bold">Increase Accuracy</span>
                      <p className="text-gray-300 text-sm">Identify subtle inconsistencies human reviewers miss</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                    <div>
                      <span className="text-white font-bold">Save Review Time</span>
                      <p className="text-gray-300 text-sm">Focus human verification only on flagged issues</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                    <div>
                      <span className="text-white font-bold">Continuous Learning</span>
                      <p className="text-gray-300 text-sm">System improves over time as it processes more documents</p>
                    </div>
                  </li>
                </ul>
              </div>

            </div>
          </div>

          <InsightCard>
            A single spreadsheet error can cost millions in a large development project. This system acts as a second set of eyes that never gets tired or distracted.
          </InsightCard>
        </div>

      </div>
    </section>
  );
};
export default WorkflowSection;




// v2 wip on showing dynamic data


// import React from 'react';
// import AgentCard from './use-case-workflow-agent-card-component';
// import ProcessOverviewCard from './use-case-workflow-process-overview-card';
// import ChatShowcase from './use-case-workflow-example-card';
// import { Workflow as WorkflowType } from '@/app/ai-use-cases/[id]/use-case.types';
// import { getColorClass, getTextColorClass } from '@/utils/tailwind';
// import UseCaseKeyBenefitsCard from './use-case-workflow-key-benefits-card';
// import { motion } from 'framer-motion';

// const InsightCard = ({ children }: { children: React.ReactNode }) => (
//   <motion.div
//     className="relative bg-gradient-to-r from-fluxos-dark-2 to-fluxos-dark-3 p-6 rounded-lg my-8 shadow-md border border-gray-700"
//     initial={{ opacity: 0, x: -20 }}
//     animate={{ opacity: 1, x: 0 }}
//     transition={{ duration: 0.5, delay: 0.2 }}
//   >
//     {/* Top border as a separate element for cleaner styling */}
//     <div className="absolute top-0 left-0 right-0 h-2 bg-fluxos-yellow"></div>
//     <div className="flex items-center mb-3 pt-2">
//       <span className="text-fluxos-yellow font-bold text-lg">Howthef Insight:</span>
//       <span className="ml-2 bg-fluxos-yellow text-black text-xs px-3 py-1 rounded-full">Pro Tip</span>
//     </div>
//     <p className="text-fluxos-gray text-lg">{children}</p>
//   </motion.div>
// );

// export { InsightCard };

// const TechnologyStackCard = ({ technologies }: { technologies: { category: string; items: string[]; icon: React.ReactNode }[] }) => (
//   <div className="bg-gray-800 rounded-lg p-6 shadow-md mt-8">
//     <h4 className="text-lg font-bold text-white mb-4">Key Technologies</h4>
//     <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
//       {technologies.map((tech, index) => (
//         <div key={index} className="bg-gray-900 p-4 rounded-lg">
//           <div className="flex items-center mb-3">
//             {tech.icon}
//             <h5 className="text-white font-bold ml-2">{tech.category}</h5>
//           </div>
//           <div className="flex flex-wrap gap-2">
//             {tech.items.map((item, idx) => (
//               <span key={idx} className="bg-gray-800 text-xs text-white px-3 py-1 rounded">{item}</span>
//             ))}
//           </div>
//         </div>
//       ))}
//     </div>
//   </div>
// );
// interface WorkflowSectionProps {
//     workflows: WorkflowType[];
// }

// const WorkflowSection: React.FC<WorkflowSectionProps> = ({ workflows }) => {
//     return (
//         <section className="py-16 bg-gray-850">
//             <div className="max-w-6xl mx-auto px-6">
//                 <div className="flex justify-between items-center mb-12">
//                      <h2  className={`text-3xl font-bold mb-4 relative after:content-[''] after:absolute after:bottom-[-8px] after:left-0 after:h-2 after:bg-yellow-500 after:w-[120%] ${getTextColorClass('white')}`}>
//                         1) Workflows in action
//                     </h2>
//                 </div>

//                 {workflows.map((workflow) => (
//                     <div key={workflow.id} className="mb-16">
//                         <div className="mb-8 rounded-lg p-8 bg-gray-900 shadow-lg border border-gray-700">
//                             {/* Render components based on JSON */}
//                             {workflow.components.map((component, index) => {
//                                 switch (component.type) {
//                                     case "sectionTitle":
//                                         return <h3 key={index}  className={`text-2xl font-bold mb-4  ${getTextColorClass('white')}`}>{component.title}</h3>;
//                                     case "textBlock":
//                                         return <p key={index} className={`mb-6 ${getTextColorClass('gray', '300')}`}>{component.content}</p>
//                                     case "processOverviewCard":
//                                         return (
//                                             <ProcessOverviewCard
//                                                 key={index}
//                                                 title={component.title}
//                                                 steps={component.steps}
//                                             />
//                                         );
//                                     case "agentCard":
//                                         return (
//                                             <AgentCard
//                                                 key={index}
//                                                 title={component.title}
//                                                 agentType={component.agentType}
//                                                 description={component.description}
//                                                 bulletPoints={component.bulletPoints}
//                                                 model={component.model}
//                                                 temperature={component.temperature}
//                                                 trigger={component.trigger}
//                                                 output={component.output}
//                                                 borderColor={component.borderColor}
//                                             />
//                                         );
//                                     case "chatShowcase":
//                                         return (
//                                             <ChatShowcase
//                                                 key={index}
//                                                 title={component.title}
//                                                 description={component.description}
//                                                 userQuery={component.userQuery}
//                                                 aiThinkingText={component.aiThinkingText}
//                                                 aiResponseIntro={component.aiResponseIntro}
//                                                 results={component.results}
//                                                 aiResponseOutro={component.aiResponseOutro}
//                                             />
//                                         );
//                                     case "keyBenefitsCard":
//                                         return (
//                                           <UseCaseKeyBenefitsCard
//                                             key={index}
//                                             title={component.title}
//                                             benefits={component.benefits}
//                                             iconColor={component.iconColor}
//                                           />
//                                         );
//                                      case "insightCard":
//                                         return (
//                                             // We create here the insight card to use the theming.
//                                             <div key={index} className={`relative bg-gradient-to-r from-fluxos-dark-2 to-fluxos-dark-3 p-6 rounded-lg my-8 shadow-md border border-gray-700 ${getTextColorClass('gray')}`}>
//                                                 {/* Top border as a separate element for cleaner styling */}
//                                                 <div className={`absolute top-0 left-0 right-0 h-2 ${getColorClass("yellow")}`}></div>
//                                                 <div className="flex items-center mb-3 pt-2">
//                                                 <span className={`${getTextColorClass("yellow")} font-bold text-lg`}>Howthef Insight:</span>
//                                                 <span className={`ml-2 ${getColorClass("yellow")} text-black text-xs px-3 py-1 rounded-full`}>Pro Tip</span>
//                                                 </div>
//                                                 <p className={`text-lg`}>{component.content}</p>
//                                             </div>
//                                         )
//                                     default:
//                                         return null;
//                                 }
//                             })}
//                         </div>
//                     </div>
//                 ))}
//             </div>
//         </section>
//     );
// };

// export default WorkflowSection;

